/* eslint-disable no-debugger */
import { useLazyQuery } from "@apollo/client";
import { List, Skeleton, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import { capitalize } from "lodash";
import isEmpty from "lodash/isEmpty";
import isUndefined from "lodash/isUndefined";
import orderBy from "lodash/orderBy";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { useDispatch, useSelector } from "react-redux";
import { UserContext } from "../../amplify/authenticator-provider";
import { APPOINTMENT_LIST } from "../../graphql/query";
import useLang from "../../hooks/useLang";
import { AppointmentIconDots } from "../../icons/js/AppointmentIcon";
import { getRootOppTypeId } from "../../library/oppTypeScoping";
import { KNOWN_ROOT_IDS, getRootConfig } from "../../library/oppTypeRootConfig";
import { openDialog } from "../../library/helpers";
import { TabContext } from "../../library/tabs";
import { convertUtcToLocal, parseVehicles } from "../../library/utils";
import {
  CALENDAR_VIEW,
  CUSTOM,
  reducerSetPagination,
  APPOINTMENT_DATE_MODE,
} from "../../redux/slices/appointmentSlice";
import usePrevious from "../../hooks/usePrevious";
import { DATE_TIME_FORMATS } from "../../utils/dateTimes";

const AppointmentList = ({
  searchKey = "",
  leadId,
  canView = true,
  leadOpportunities,
  selectedLead = {},
  onClick = () => {},
  count,
  isFromActiviytCenter = false,
  handleLeadDetailProfile,
  appointmentsData = [],
  isFromAppointment = false,
  // filters,
  passUserIds = true,
  refreshLeadListData,
  sorting,
  // Opp-type-scoped tab — root opp_type id of the active tab.
  // BE filters appointments to those tied to a LeadOpportunity whose
  // root opp_type matches via the existing ``opportunityTypeId`` arg
  // on ``getUserAppointment``. ``null`` ⇒ no tab filter.
  selectedOppTypeId,
}) => {
  const { filters } = useSelector(state => state.appointments);
  const {
    userIds,
    appointmentStatus,
    range,
    userMode,
    page,
    pageSize,
    customRange,
    dateType,
    viewMode,
    opportunitySourceIds,
    entityMode,
    activeFilterMode,
    teamIds,
  } = filters;
  // Drive the payload off the committed mode (`activeFilterMode`), not the
  // visible toggle, so a stray preview-only toggle doesn't change the query.
  const isTeamsMode = (activeFilterMode || entityMode) === "teams";
  const isCustomRangeMode = dateType === CUSTOM;
  const hasCustomRangeDates = !!customRange?.startDate && !!customRange?.endDate;
  const effectiveRange = isCustomRangeMode ? customRange : range;
  const shouldSkipRangeQuery = isCustomRangeMode && !hasCustomRangeDates;
  // Per BE contract, startDate/endDate are only honored for an explicit window
  // — Custom (filterType null) and Calendar view. Named presets send just
  // filterType and let the backend derive the range.
  const usesExplicitRange = isCustomRangeMode || dateType === CALENDAR_VIEW;
  const dispatch = useDispatch();
  const { user, refetchAppointments, setrefetchAppointments } = useContext(UserContext);
  const { setActiveTab } = useContext(TabContext);
  const [lang] = useLang();
  const [appointments, setAppointments] = useState([]);
  const [openAppointmentPopup, setAppointmentPopupVisible] = useState(false);
  const [appointment, setAppointment] = useState({});
  const [defaultDate, setDefaultDate] = useState(null);
  const [totalCount, setCount] = useState(0);
  const [variables, setVariables] = useState({
    userIds,
    search: searchKey,
    page: 1,
    pageSize: 10,
    orderBy: sorting?.orderBy || "start_datetime",
    orderDirection: sorting?.orderDirection || "desc",
    dateFilter: null,
    companyId: user?.company?.id || null,
    leadId: +leadId || null,
    leadOpportunities: [],
  });

  const prevState = usePrevious(leadId);

  useEffect(() => {
    const temp = { ...filters };
    // delete temp.startDate;
    // delete temp.endDate;
    temp.page = 1;
    temp.pageSize = 10;
    temp.orderBy = sorting?.orderBy || variables.orderBy || "id";
    temp.orderDirection = sorting?.orderDirection || variables.orderDirection || "asc";
    temp.dateFilter = null;
    temp.leadOpportunities = [];
    temp.leadId = +leadId;
    // temp.createdByUserIds = createdByUserIds === "All" ? null : createdByUserIds;
    setVariables(
      { ...temp } || {
        page: 1,
        search: searchKey,
        pageSize: 10,
        orderBy: "start_datetime",
        orderDirection: "desc",
        dateFilter: null,
        companyId: user?.company?.id || null,
        leadId: +leadId || null,
        leadOpportunities: [],
      }
    );
  }, [leadId, user, filters]);

  const handleAppointmentModalClose = (status, isPopClosed = true) => {
    setAppointmentPopupVisible(false);
    setAppointment(null);
    setDefaultDate(null);
    if (isPopClosed) {
      // eslint-disable-next-line no-use-before-define
      // getAppointments({
      //   ...variables,
      // });
      setVariables({
        ...variables,
        companyId: user?.company?.id || null,
      });
    }
  };

  const [getAppointments, { loading, refetch: refetchAppointmentsQuery }] = useLazyQuery(
    APPOINTMENT_LIST,
    {
      fetchPolicy: "network-only",
      onCompleted: resp => {
        const appointments = (resp?.getUserAppointment?.data || []).map(el => ({
          ...el,
          type:
            el.appointmentStatus === "CANCELLED"
              ? "error"
              : el.appointmentStatus === "SHOWED"
              ? "show"
              : el.appointmentStatus === "NO_SHOWED"
              ? "noshow"
              : el.isConfirmed
              ? "success"
              : el.appointmentStatus === "SCHEDULED"
              ? "scheduled"
              : el.appointmentStatus === "RESCHEDULED"
              ? "rescheduled"
              : "default",
          content: el.summary,
        }));
        const totalCount = resp?.getUserAppointment?.count || 0;
        setAppointments(appointments);
        setCount(totalCount);
      },
    }
  );

  useEffect(() => {
    if (isFromAppointment) {
      setAppointments(appointmentsData);
      setCount(count);
    }
  }, [appointmentsData]);

  const fetchAppointments = () => {
    if (shouldSkipRangeQuery) return;
    if (leadId) {
      getAppointments({
        variables: {
          ...(passUserIds && {
            startDate: effectiveRange.startDate,
            endDate: effectiveRange.endDate,
          }),
          companyId: user?.company?.id,
          ...(passUserIds && {
            userIds: userMode === 1 ? userIds : null,
            createdByUserIds: userMode === 0 ? userIds : null,
          }),
          appointmentStatus: appointmentStatus,
          orderBy: sorting?.orderBy || "id",
          orderDirection: sorting?.orderDirection || "asc",
          leadOpportunities: [],
          page: page,
          pageSize: pageSize,
          search: searchKey,
          leadId: +leadId || +selectedLead?.id,
          // Opp-type-scoped tab filter (lead-scoped path only).
          opportunityTypeId: selectedOppTypeId || null,
        },
      });
    } else if (!isFromAppointment && !isFromActiviytCenter) {
      // Calendar sidebar — fetch with server-side pagination, no leadId
      getAppointments({
        variables: {
          // Named presets omit start/end so a stale calendar range can't
          // override the filterType-derived range (matches the calendar query).
          startDate: usesExplicitRange ? effectiveRange.startDate : null,
          endDate: usesExplicitRange ? effectiveRange.endDate : null,
          companyId: user?.company?.id,
          // Teams mode swaps the user-side filters out for `teamIds`; users
          // mode keeps the existing assigned/created split.
          userIds: isTeamsMode ? null : userMode === 1 ? userIds : null,
          createdByUserIds: isTeamsMode ? null : userMode === 0 ? userIds : null,
          teamIds: isTeamsMode && teamIds?.length > 0 ? teamIds : null,
          appointmentStatus: appointmentStatus,
          orderBy: sorting?.orderBy || "start_datetime",
          orderDirection: sorting?.orderDirection || "desc",
          page: page,
          pageSize: pageSize,
          search: searchKey,
          filterDateType: filters?.dateMode || APPOINTMENT_DATE_MODE.DUE_ON,
          // In CUSTOM mode send filterType: null so the backend uses the
          // explicit startDate/endDate without applying a named preset.
          filterType: isCustomRangeMode ? null : filters?.dateType || CALENDAR_VIEW,
          opportunityTypeId: filters?.opportunityTypeId ? +filters?.opportunityTypeId : null,
          // Carried over from dashboard drill-ins. `viewMode` mirrors the
          // waterfall / current-status toggle; `opportunitySourceIds` is set
          // when the user clicked into "by source", empty otherwise.
          viewMode: viewMode || null,
          opportunitySourceIds:
            Array.isArray(opportunitySourceIds) && opportunitySourceIds.length > 0
              ? opportunitySourceIds
              : null,
        },
      });
    }
  };

  useEffect(() => {
    if (leadId || (!isFromAppointment && !isFromActiviytCenter)) {
      fetchAppointments();
    }
    // selectedOppTypeId added so the lead-scoped query refetches when
    // the user switches activity-center tabs.
  }, [filters, searchKey, leadId, refetchAppointments, selectedOppTypeId]);

  const handleAppointmentClick = item => {
    if (isFromActiviytCenter) {
      setAppointment(item);
      openDialog("appointmentForm", {
        dialogHeader: lang.editAppointment,
        lead: selectedLead,
        visibleModal: openAppointmentPopup,
        appointment: item,
        defaultDate: defaultDate,
        refetchAppointmentList: refetchAppointmentsQuery,
        setAppointmentModelVisible: handleAppointmentModalClose,
        refreshLeadListData,
        leadOpportunities: leadOpportunities,
        redirect: (e, updateDate) => {
          if (isEmpty(selectedLead) && e) {
            setActiveTab("activity-center");
            onClick({
              data: item?.lead || selectedLead,
              tab: "activity-center",
            });
          } else {
            onClick(item?.lead || selectedLead, true, updateDate);
          }
        },
        activeAppointments: (appointments || []).filter(el =>
          ["default", "processing"].includes(el.type)
        ),
        handleLeadDetailProfile: handleLeadDetailProfile,
      });
      setAppointmentPopupVisible(true);
    }
  };

  const vehicles = vehicles => {
    let list = [];
    if (vehicles.length > 0) {
      vehicles = orderBy(vehicles, ["isCurrent"], ["asc"]);
      list = vehicles.map(el => {
        let voi = "";
        if (!isUndefined(el.year) && el.year !== undefined && el.year !== null && el.year !== 0)
          voi = `${voi + el.year} `;
        if (!isUndefined(el.make) && el.make !== null && el.make !== undefined && el.make !== 0)
          voi = `${voi + el.make} `;
        if (!isUndefined(el.model) && el.model !== null && el.model !== undefined && el.model !== 0)
          voi = `${voi + el.model} `;

        return voi === "" || voi === 0 ? (
          <span>{lang.noVehicles}</span>
        ) : (
          <Tooltip
            placement="topLeft"
            title={el?.isCurrent ? lang.previousOrCurrentVehicle : lang.vehicleOfInterest}
          >
            <Tag className="w-[99%] truncate" color={el?.isCurrent ? "magenta" : "blue"}>
              {voi}
            </Tag>
          </Tooltip>
        );
      });
    }
    return list;
  };
  const discussedVoi = (lead, voiId) => {
    if (lead?.leadVehicleInventory?.length > 0) {
      const vehicleInventoryObj = lead.leadVehicleInventory.find(item => +item.id === +voiId);
      if (vehicleInventoryObj) {
        let voi = "";
        if (
          !isUndefined(vehicleInventoryObj.make) &&
          vehicleInventoryObj.make !== null &&
          vehicleInventoryObj.make !== undefined &&
          vehicleInventoryObj.make !== 0
        )
          voi = `${voi} ${vehicleInventoryObj.make} `;
        if (
          !isUndefined(vehicleInventoryObj.model) &&
          vehicleInventoryObj.model !== null &&
          vehicleInventoryObj.model !== undefined &&
          vehicleInventoryObj.model !== 0
        )
          voi = `${voi} ${vehicleInventoryObj.model} `;
        if (
          !isUndefined(vehicleInventoryObj.year) &&
          +vehicleInventoryObj.year !== 0 &&
          vehicleInventoryObj.year !== null &&
          vehicleInventoryObj.year !== undefined &&
          +vehicleInventoryObj.year !== 0
        )
          voi = `${voi} ${vehicleInventoryObj.year} `;
        return (
          <Tooltip title={lang.vehicleOfInterest}>
            <Tag color="blue">{voi || "No vehicles"}</Tag>
          </Tooltip>
        );
      }
    } else if (lead?.vehicleOfInterest?.length > 0) {
      const vehicleOfInterestObj = lead.vehicleOfInterest.find(item => +item.id === +voiId);
      if (vehicleOfInterestObj) {
        let voi = "";
        if (
          !isUndefined(vehicleOfInterestObj.year) &&
          +vehicleOfInterestObj.year !== 0 &&
          vehicleOfInterestObj.year !== null &&
          vehicleOfInterestObj.year !== undefined &&
          +vehicleOfInterestObj.year !== 0
        )
          voi = `${voi} ${vehicleOfInterestObj.year} `;
        if (
          !isUndefined(vehicleOfInterestObj.make) &&
          vehicleOfInterestObj.make !== null &&
          vehicleOfInterestObj.make !== undefined &&
          vehicleOfInterestObj.make !== 0
        )
          voi = `${voi} ${vehicleOfInterestObj.make} `;
        if (
          !isUndefined(vehicleOfInterestObj.model) &&
          vehicleOfInterestObj.model !== null &&
          vehicleOfInterestObj.model !== undefined &&
          vehicleOfInterestObj.model !== 0
        )
          voi = `${voi} ${vehicleOfInterestObj.model} `;
        return (
          <Tooltip title={lang.previousOrCurrentVehicle}>
            <Tag color="magenta">{voi || "No vehicles"}</Tag>
          </Tooltip>
        );
      }
    }
  };

  return (
    <Skeleton loading={loading} active avatar>
      <List
        itemLayout="horizontal"
        dataSource={appointments}
        pagination={
          totalCount < 5
            ? null
            : {
                size: "small",
                current: page,
                pageSize: pageSize,
                total: totalCount,
                showTotal: total =>
                  `Total: ${total} ${
                    total === 1 ? lang.appointment.toLowerCase() : lang.appointments.toLowerCase()
                  }`,
                pageSizeOptions: ["5", "25", "50"],
                showSizeChanger: true,
                onChange: (page, pageSize) => {
                  dispatch(reducerSetPagination({ page: page, pageSize: pageSize }));
                },
                onShowSizeChange: (page, pageSize) => {
                  dispatch(reducerSetPagination({ page: page, pageSize: pageSize }));
                },
              }
        }
        renderItem={(item, index) => {
          return (
            <Appointment
              key={index}
              index={index}
              item={item}
              handleAppointmentClick={handleAppointmentClick}
            />
          );
        }}
      />
    </Skeleton>
  );
};
export default AppointmentList;
function Appointment({ index, item, handleAppointmentClick }) {
  const parsedVehicles = useMemo(() => parseVehicles(item.discussedVoi), [item]);
  const status = status => {
    switch (status) {
      case "error":
        return { text: "Cancelled", color: "#cf1322" };
      case "success":
        return { text: "Confirmed", color: "#08979c" };
      case "scheduled":
        return { text: "Scheduled", color: "#0958d9" };
      case "rescheduled":
        return { text: "Rescheduled", color: "#531dab" };
      case "default":
        return { text: "Default", color: "#595959" };
      case "noshow":
        return { text: "No-Showed", color: "#595959" };
      case "show":
        return { text: "Showed", color: "#A7F2CF" };
      default:
        return { text: "Scheduled Appointment", color: "#0958d9" };
    }
  };
  const TooltipTitle = () => {
    return (
      <div className="flex-col-centered">
        <span>Assigned To: {item?.user?.fullName || "N/A"}</span>
        <span>Creator: {item?.createdBy?.fullName || "N/A"}</span>
      </div>
    );
  };
  const tagColor = interest => {
    switch (interest) {
      case "BUY":
        return "green";
      case "SELL":
        return "blue";
      case "TRADE":
        return "orange";
      case "SERVICE":
        return "purple";
      default:
        return "grey";
    }
  };
  // Top-right opp-type chip — mirrors ``OppTypeCardCornerIcon`` on the
  // opportunity card so the visual language is the same across the
  // panel. Hidden when the appointment isn't tied to a known root.
  const opportunityType = item?.leadOpportunity?.opportunityType;
  const rootOppTypeId = getRootOppTypeId(opportunityType);
  const showRootChip = rootOppTypeId && KNOWN_ROOT_IDS.has(Number(rootOppTypeId));
  const rootCfg = showRootChip ? getRootConfig(rootOppTypeId) : null;
  const RootChipIcon = rootCfg?.Icon;

  return (
    <div
      key={index}
      className="flex-col-centered mb-1 items-start p-4 border-[1px] border-solid border-grey-200 rounded-[4px] relative w-full overflow-hidden"
    >
      {showRootChip && (
        <span
          className="absolute right-0 top-0 flex items-center justify-center"
          style={{
            width: 20,
            height: 20,
            background: rootCfg.chipBg,
            borderBottomLeftRadius: 4,
          }}
        >
          <RootChipIcon size={13} color={rootCfg.stroke} strokeWidth={1.33} />
        </span>
      )}
      <div className="flex-col-centered gap-1">
        <div className="flex-row-centered gap-1">
          <div
            onClick={() => handleAppointmentClick(item)}
            className="p-2 cursor-pointer rounded-full flex-row-centered justify-center border-solid border-[2px]"
            style={{ borderColor: status(item.type).color }}
          >
            <AppointmentIconDots />
          </div>
          <div className="flex-col-centered items-start">
            <span className="text-sm font-[600] text-grey-700">
              {convertUtcToLocal(
                item.startDatetime,
                item?.extraData?.is_tentative_appointment_time ? "DD MMM" : "DD MMM, hh:mm A",
                item.timezone
              )}
            </span>
            <span
              className="text-[14px] font-[400] flex flex-row items-center"
              style={{ color: status(item.type).color }}
            >
              {status(item.type).text}
            </span>
          </div>
        </div>
        <div className="flex-row-centered items-centered gap-1">
          <span className="text-sm font-[600] text-grey-700">Created at:</span>
          <Tooltip title={<TooltipTitle />}>
            <span className="text-sm font-[400] text-grey-800">
              {convertUtcToLocal(
                item?.dateCreated,
                DATE_TIME_FORMATS.DD_MMM_YYYY_hh_mm_A,
                item.timezone
              )}
            </span>
          </Tooltip>
        </div>
        <div className="flex-row-centered items-centered gap-1">
          <span className="text-sm font-[600] text-grey-700">Assigned to:</span>
          <Tooltip title={<TooltipTitle />}>
            <span className="text-sm font-[400] text-grey-800">{item?.user?.fullName}</span>
          </Tooltip>
        </div>
        <div className="flex-row-centered items-centered gap-1">
          <span className="text-sm font-[600] text-grey-700">Lead Name:</span>
          <Tooltip
            title={
              <div className="flex-col-centered">
                <span className="">Lead Name: {item?.lead?.fullName || "N/A"}</span>
                <span>
                  Phones:{" "}
                  {item?.lead?.phoneNumbers && item?.lead?.phoneNumbers.length > 0
                    ? formatPhoneNumberIntl(item?.lead?.phoneNumbers[0].phone)
                    : "N/A"}
                </span>
                <span>
                  Emails:{" "}
                  {item?.lead?.emails && item?.lead?.emails?.length > 0
                    ? item?.lead?.emails[0]?.email
                    : "N/A"}{" "}
                </span>
              </div>
            }
          >
            <span className="text-sm font-[400] text-grey-800">{item?.lead?.fullName}</span>
          </Tooltip>
        </div>
        <div className="flex-col-centered items-start gap-0.5">
          <span className="text-sm font-[600] text-grey-700">Vehicle of interest:</span>
          <Tooltip title={capitalize(item?.discussedVoi?.customerInterest || "")}>
            <Tag color={tagColor(item?.discussedVoi?.customerInterest || "")}>
              {isEmpty(parsedVehicles) ? "No Vehicles" : parsedVehicles}
            </Tag>
          </Tooltip>
        </div>
      </div>
      {item?.description && (
        <>
          <div className="h-[1px] mt-3 mb-2 w-full bg-grey-300" />
          <div>{item.description || "N/A"}</div>
        </>
      )}
      <div className="flex-col-centered gap-1 items-start justify-start">
        <span className="xs-[400] text-grey-500">
          Created on: {convertUtcToLocal(item.dateCreated, "MMM DD YYYY, hh:mm A", item.timezone)}
        </span>
        <span className="xs-[400] text-grey-500">
          Updated on: {convertUtcToLocal(item.dateUpdated, "MMM DD YYYY, hh:mm A", item.timezone)}
        </span>
      </div>
    </div>
  );
}
